import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faBuilding, faEnvelope, faPhone, faMapMarkerAlt, faIndustry,
  faSyncAlt, faPlus, faEdit, faTrash, faCheckCircle, faExclamationTriangle,
  faBriefcase, faTimes, faFilter, faSortDown, faSortUp, faEye, faChevronLeft, faChevronRight,
  faShare, faUsers, faUserCheck
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie';
import { PartenairesContext } from '../layout/CunstomPartenaires';
import { Link } from 'react-router-dom';
import CandidatsPlaces from './CandidatsPlaces';

// ============================================================
// CACHE DEXIE (24h) avec possibilité de récupérer le cache périmé
// ============================================================
const db = new Dexie('EntreprisesCacheDB');
db.version(1).stores({ entreprises: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedEntreprises(key, allowExpired = false) {
  try {
    const entry = await db.entreprises.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}
async function setCachedEntreprises(key, data) {
  await db.entreprises.put({ key, data, timestamp: Date.now() });
}
async function getStaleEntreprises(key) {
  return getCachedEntreprises(key, true);
}

// ============================================================
// DÉDOUBLONNAGE DES REQUÊTES EN COURS (Map globale)
// ============================================================
const pendingFetches = new Map();

const API_ENTREPRISE_URL = `${API_CONFIG2.apiUrl}entreprises/api_entreprise.php`;
const API_OFFRE_URL = `${API_CONFIG2.apiUrl}offres/api_offres.php`;

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const ManageEntreprisesPlacement = () => {
  const context = useContext(PartenairesContext) || {};
  const { refreshKey, setSource } = context;

  // États principaux
  const [entreprises, setEntreprises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 10
  });
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [dataSource, setDataSource] = useState('cache');

  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);

  // --- Modal entreprise ---
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    nomEntreprise: '',
    email: '',
    telephone: '',
    adresse: '',
    secteur_activite: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // --- Modal offre d'emploi ---
  const [showOffreModal, setShowOffreModal] = useState(false);
  const [offreEditingId, setOffreEditingId] = useState(null);
  const [offreForm, setOffreForm] = useState({
    id_entreprise: '',
    description: '',
    sexe: '',
    moyen_age: '',
    competences: '',
    niveau_etude: '',
    experience_professionnelle: '',
    type_contrat: '',
    salaire: ''
  });
  const [offreFormErrors, setOffreFormErrors] = useState({});

  // --- Modal Candidats Placés ---
  const [showCandidatsModal, setShowCandidatsModal] = useState(false);
  const [selectedEntrepriseId, setSelectedEntrepriseId] = useState(null);
  const [selectedEntrepriseNom, setSelectedEntrepriseNom] = useState('');

  // ============================================================
  // GESTION DES TOASTS
  // ============================================================
  const showToast = useCallback((message, variant = 'success', duration = 5000) => {
    setToast({ show: true, message, variant });
    setTimeout(() => {
      if (isMounted.current) setToast(prev => ({ ...prev, show: false }));
    }, duration);
  }, []);

  // ============================================================
  // RÉCUPÉRATION DES ENTREPRISES (avec dédoublonnage + annulation)
  // ============================================================
  const fetchEntreprises = useCallback(async (page = 1, search = '', forceRefresh = false, retryCount = 0) => {
    // Annuler la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = `page_${page}_search_${search}`;
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    // Dédoublonnage : si une requête identique est en cours, on l'attend
    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setEntreprises(result.data);
          setPagination(result.pagination);
          setDataSource('cache (partagé)');
          if (setSource) setSource('cache (partagé)');
          setLoading(false);
          setRefreshing(false);
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    // Limiter les tentatives pour éviter les boucles infinies
    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        showToast('Erreur : trop de tentatives. Veuillez réessayer plus tard.', 'danger');
      }
      return;
    }

    // Définir l'état de chargement
    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    const fetchPromise = (async () => {
      try {
        // 1. Cache (sauf forceRefresh)
        if (!forceRefresh) {
          const cached = await getCachedEntreprises(cacheKey);
          if (cached && isMounted.current) {
            setEntreprises(cached.data);
            setPagination(cached.pagination);
            setDataSource('cache');
            if (setSource) setSource('cache');
            setLoading(false);
            setRefreshing(false);
            return cached;
          }
        }

        // 2. Requête réseau
        const uniqueParam = new Date().getTime();
        const url = `${API_ENTREPRISE_URL}?page=${page}&limit=${pagination.limit}&search=${encodeURIComponent(search)}&unique=${uniqueParam}`;
        const response = await fetch(url, { signal: controller.signal });

        // Gestion 429
        if (response.status === 429) {
          const stale = await getStaleEntreprises(cacheKey);
          if (stale && isMounted.current) {
            setEntreprises(stale.data);
            setPagination(stale.pagination);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            showToast('429 – Utilisation du cache périmé', 'warning');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          // Sinon, backoff exponentiel
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchEntreprises(page, search, forceRefresh, retryCount + 1);
        }

        if (!response.ok) {
          const stale = await getStaleEntreprises(cacheKey);
          if (stale && isMounted.current) {
            setEntreprises(stale.data);
            setPagination(stale.pagination);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            showToast(`Erreur réseau – Utilisation du cache`, 'warning');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (data.status === 'success') {
          const result = { data: data.data, pagination: data.pagination };
          if (isMounted.current) {
            setEntreprises(data.data);
            setPagination(data.pagination);
            const source = forceRefresh ? 'live (forcé)' : 'live';
            setDataSource(source);
            if (setSource) setSource(source);
            await setCachedEntreprises(cacheKey, result);
            if (forceRefresh) {
              showToast('Données actualisées', 'success');
            }
            setLoading(false);
            setRefreshing(false);
          }
          return result;
        } else {
          throw new Error(data.message || 'Erreur inconnue');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        // Dernier recours : cache périmé
        const stale = await getStaleEntreprises(cacheKey);
        if (stale && isMounted.current) {
          setEntreprises(stale.data);
          setPagination(stale.pagination);
          setDataSource('cache (périmé)');
          if (setSource) setSource('cache (périmé)');
          showToast(`Erreur – Utilisation du cache : ${err.message}`, 'warning');
          setLoading(false);
          setRefreshing(false);
          return stale;
        }
        if (isMounted.current) {
          showToast(err.message || 'Erreur de chargement', 'danger');
          setLoading(false);
          setRefreshing(false);
        }
        throw err;
      }
    })();

    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [pagination.limit, showToast, setSource]);

  // ============================================================
  // RÉACTION AU REFRESH GLOBAL (via contexte)
  // ============================================================
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      fetchEntreprises(pagination.currentPage, searchTerm, true);
    }
  }, [refreshKey, fetchEntreprises, pagination.currentPage, searchTerm]);

  // ============================================================
  // GESTION DE LA RECHERCHE (debounce)
  // ============================================================
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchEntreprises(1, val, false);
    }, 400);
  };

  // ============================================================
  // CHARGEMENT INITIAL ET NETTOYAGE
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    fetchEntreprises();
    return () => {
      isMounted.current = false;
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
      pendingFetches.clear();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================
  // GESTION FORMULAIRE ENTREPRISE
  // ============================================================
  const resetForm = () => {
    setForm({ nomEntreprise: '', email: '', telephone: '', adresse: '', secteur_activite: '' });
    setFormErrors({});
    setEditingId(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!form.nomEntreprise.trim()) errors.nomEntreprise = 'Le nom est requis';
    if (!form.email.trim()) errors.email = 'L\'email est requis';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = 'Email invalide';
    if (!form.telephone.trim()) errors.telephone = 'Le téléphone est requis';
    if (!form.adresse.trim()) errors.adresse = 'L\'adresse est requise';
    if (!form.secteur_activite.trim()) errors.secteur_activite = 'Le secteur est requis';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (entreprise) => {
    setForm({
      nomEntreprise: entreprise.nomEntreprise || '',
      email: entreprise.email || '',
      telephone: entreprise.telephone || '',
      adresse: entreprise.adresse || '',
      secteur_activite: entreprise.secteur_activite || ''
    });
    setEditingId(entreprise.id);
    setFormErrors({});
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      nomEntreprise: form.nomEntreprise.trim(),
      email: form.email.trim(),
      telephone: form.telephone.trim(),
      adresse: form.adresse.trim(),
      secteur_activite: form.secteur_activite.trim()
    };

    try {
      let res;
      if (editingId) {
        res = await fetch(`${API_ENTREPRISE_URL}?id=${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        res = await fetch(API_ENTREPRISE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }
      const data = await res.json();
      if (!res.ok || data.status === 'error') throw new Error(data.message || 'Erreur');
      showToast(editingId ? 'Entreprise modifiée ✓' : 'Entreprise créée ✓', 'success');
      setShowModal(false);
      resetForm();
      fetchEntreprises(pagination.currentPage, searchTerm, true);
    } catch (err) {
      showToast(err.message, 'danger');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('⚠️ Supprimer définitivement cette entreprise ? Cette action est irréversible.')) return;
    try {
      const res = await fetch(`${API_ENTREPRISE_URL}?id=${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok || data.status === 'error') throw new Error(data.message || 'Erreur');
      showToast('Entreprise supprimée ✓', 'success');
      fetchEntreprises(pagination.currentPage, searchTerm, true);
    } catch (err) {
      showToast(err.message, 'danger');
    }
  };

  // ============================================================
  // GESTION MODAL CANDIDATS PLACÉS
  // ============================================================
  const openCandidatsModal = (entrepriseId, entrepriseNom) => {
    setSelectedEntrepriseId(entrepriseId);
    setSelectedEntrepriseNom(entrepriseNom);
    setShowCandidatsModal(true);
  };

  // ============================================================
  // RENDU AVEC CARTES (CARD LAYOUT)
  // ============================================================
  if (loading && entreprises.length === 0) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="text-secondary mt-2">Chargement des entreprises...</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* ============================================================
           DARK BLUE GLASSMORPHISM – MANAGE ENTREPRISES (CARD LAYOUT)
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        * { box-sizing: border-box; }
        .entreprise-list {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: clamp(0.75rem, 2vw, 2rem);
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: clamp(1rem, 1.5vw, 1.8rem);
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
          margin-bottom: 24px;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }

        .badge-source {
          font-size: clamp(0.6rem, 0.9vw, 0.75rem);
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
          font-weight: 500;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .monetra-btn {
          padding: 8px 16px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn:disabled {
          opacity: 0.4;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.danger {
          background: rgba(239, 68, 68, 0.12);
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }
        .monetra-btn.danger:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.25);
        }
        .monetra-btn.icon-only {
          padding: 6px 10px;
          min-width: 36px;
          justify-content: center;
        }

        .search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 4px 4px 4px 16px;
          transition: all 0.3s ease;
          flex: 1;
          min-width: 180px;
          max-width: 100%;
        }
        .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.08);
        }
        .search-wrapper input {
          background: transparent;
          border: none;
          color: var(--text-primary);
          outline: none;
          padding: 10px 0;
          flex: 1;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .search-wrapper input::placeholder {
          color: var(--text-muted);
        }

        /* ----- CARD GRID ----- */
        .cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1.5rem;
          padding: 0.5rem 0;
        }

        .entreprise-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 20px;
          padding: 1.2rem;
          transition: all 0.25s ease;
          display: flex;
          flex-direction: column;
          position: relative;
          overflow: hidden;
        }
        .entreprise-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0; height: 3px;
          background: var(--accent-blue);
          opacity: 0;
          transition: opacity 0.3s;
        }
        .entreprise-card:hover {
          transform: translateY(-4px);
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 36px rgba(59, 130, 246, 0.08);
          background: rgba(255,255,255,0.06);
        }
        .entreprise-card:hover::before {
          opacity: 1;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 0.8rem;
        }
        .card-title {
          font-size: clamp(1rem, 1.3vw, 1.2rem);
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .card-title .badge-id {
          font-size: clamp(0.6rem, 0.8vw, 0.7rem);
          background: var(--accent-blue-dim);
          color: var(--accent-blue);
          padding: 2px 10px;
          border-radius: 30px;
          font-weight: 500;
        }

        .card-body {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
        }
        .card-body .info-row {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: clamp(0.8rem, 0.95vw, 0.9rem);
          color: var(--text-secondary);
        }
        .card-body .info-row .icon {
          width: 20px;
          color: var(--text-muted);
          font-size: clamp(0.8rem, 1vw, 0.9rem);
        }
        .card-body .sector-badge {
          display: inline-block;
          margin-top: 4px;
          padding: 4px 14px;
          background: var(--accent-blue-dim);
          border-radius: 30px;
          font-size: clamp(0.7rem, 0.8vw, 0.8rem);
          color: var(--accent-blue);
          font-weight: 500;
          align-self: flex-start;
        }

        .card-actions {
          display: flex;
          gap: 6px;
          margin-top: 1rem;
          padding-top: 0.8rem;
          border-top: 1px solid rgba(255,255,255,0.04);
          justify-content: flex-end;
          flex-wrap: wrap;
        }

        /* ----- PAGINATION ----- */
        .pagination-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 1.5rem;
          padding-top: 1rem;
          border-top: 1px solid rgba(255,255,255,0.04);
        }
        .pagination-bar .page-info {
          font-size: clamp(0.75rem, 1vw, 0.85rem);
          color: var(--text-secondary);
        }
        .pagination-bar .page-info strong {
          color: var(--text-primary);
        }

        /* ----- TOAST ----- */
        .toast-container {
          position: fixed;
          top: clamp(12px, 2vh, 24px);
          right: clamp(12px, 2vw, 24px);
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 10px;
          max-width: 440px;
        }
        .toast-item {
          background: rgba(10, 20, 45, 0.8);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: var(--radius);
          padding: 12px 18px;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 14px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.4);
          animation: slideIn 0.35s ease;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .toast-item.success { border-left: 4px solid var(--accent-blue); }
        .toast-item.warning { border-left: 4px solid #f59e0b; }
        .toast-item.danger { border-left: 4px solid #ef4444; }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px) scale(0.96); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 4px;
          margin-left: auto;
          transition: color 0.2s;
        }
        .btn-close-custom:hover {
          color: var(--text-primary);
        }

        /* ----- MODALES ----- */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0,0,0,0.7);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          animation: fadeIn 0.25s ease;
          padding: 16px;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .modal-content {
          background: rgba(10, 20, 45, 0.95);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          padding: clamp(1.2rem, 2vw, 2rem);
          max-width: 900px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          color: var(--text-primary);
          box-shadow: 0 24px 60px rgba(0,0,0,0.8);
          animation: scaleIn 0.25s ease;
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .modal-content h3 {
          font-weight: 600;
          margin-bottom: 1.2rem;
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: clamp(1.2rem, 2vw, 1.5rem);
        }
        .modal-content h3 .sub {
          font-weight: 400;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
          color: var(--text-secondary);
        }

        .form-group {
          margin-bottom: 1rem;
        }
        .form-group label {
          display: block;
          margin-bottom: 4px;
          font-size: clamp(0.75rem, 0.9vw, 0.85rem);
          color: var(--text-secondary);
          font-weight: 500;
        }
        .form-group label .required {
          color: #f87171;
          margin-left: 2px;
        }
        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 10px 14px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 12px;
          color: var(--text-primary);
          outline: none;
          font-family: inherit;
          font-size: clamp(0.85rem, 1.1vw, 1rem);
          transition: border 0.2s, box-shadow 0.2s;
        }
        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08);
        }
        .form-group textarea {
          min-height: 70px;
          resize: vertical;
        }
        .form-group .error-text {
          color: #f87171;
          font-size: clamp(0.7rem, 0.8vw, 0.8rem);
          margin-top: 4px;
        }
        .form-group input.error,
        .form-group select.error,
        .form-group textarea.error {
          border-color: #f87171;
          box-shadow: 0 0 0 3px rgba(239,68,68,0.15);
        }
        .form-group select option {
          background: #0b1428;
          color: var(--text-primary);
        }

        .flex-between {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }
        .gap-2 { gap: 8px; }
        .mb-3 { margin-bottom: clamp(1rem, 2vw, 1.5rem); }
        .mt-3 { margin-top: clamp(1rem, 2vw, 1.5rem); }
        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-center { text-align: center; }
        .fw-bold { font-weight: 600; }
        .small { font-size: clamp(0.7rem, 0.9vw, 0.85rem); }
        .display-6 {
          font-size: clamp(1.6rem, 4vw, 2.2rem);
          font-weight: 700;
        }
        .m-0 { margin: 0; }
        .p-3 { padding: 1rem; }
        .p-0 { padding: 0; }
        .overflow-hidden { overflow: hidden; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .border-top { border-top: 1px solid rgba(255,255,255,0.04); }
        .me-2 { margin-right: 0.5rem; }
        .mt-2 { margin-top: 0.5rem; }

        /* ---- RESPONSIVE ---- */
        @media (max-width: 768px) {
          .entreprise-list { padding: 12px; }
          .glass-card { padding: 1rem; }

          .flex-between {
            flex-direction: column;
            align-items: stretch;
          }
          .flex-between > div:first-child {
            text-align: center;
          }

          .search-wrapper {
            max-width: 100%;
            min-width: unset;
          }

          .cards-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }

          .entreprise-card {
            padding: 1rem;
          }
          .card-actions {
            justify-content: center;
          }

          .monetra-btn {
            font-size: 0.75rem;
            padding: 6px 12px;
            min-height: 38px;
            flex: 1 0 auto;
            justify-content: center;
          }
          .monetra-btn.icon-only {
            padding: 4px 8px;
            min-width: 32px;
          }

          .modal-overlay {
            padding: 8px;
          }
          .modal-content {
            padding: 1.2rem;
            max-width: 100%;
            border-radius: 20px;
          }
          .modal-content .form-group input,
          .modal-content .form-group select,
          .modal-content .form-group textarea {
            font-size: 0.9rem;
          }
          .modal-content .d-flex.justify-content-end {
            flex-direction: column;
            gap: 8px;
          }
          .modal-content .d-flex.justify-content-end button {
            width: 100%;
            justify-content: center;
          }
        }
        @media (max-width: 480px) {
          .monetra-btn {
            font-size: 0.7rem;
            padding: 4px 10px;
            min-height: 34px;
          }
          .cards-grid {
            gap: 0.8rem;
          }
          .entreprise-card {
            padding: 0.8rem;
          }
        }
      `}</style>

      <div className="entreprise-list">
        {/* Header avec badge source et bouton refresh */}
        <div className="flex-between mb-3">
          <div>
            <h1 className="display-6 fw-bold mb-1">🏢 Entreprises</h1>
            <p className="text-secondary m-0" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.95rem)' }}>
              Gérez vos entreprises et leurs offres d'emploi
            </p>
          </div>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <span className={`badge-source ${dataSource === 'live' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'cache (partagé)' ? '🔄 Partage' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button
              className="monetra-btn"
              onClick={() => fetchEntreprises(pagination.currentPage, searchTerm, false)}
              disabled={loading || refreshing}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} /> <span className="label">Actualiser</span>
            </button>
            <button
              className="monetra-btn"
              onClick={() => fetchEntreprises(pagination.currentPage, searchTerm, true)}
              disabled={loading || refreshing}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} style={{ color: '#ef4444' }} /> <span className="label">Forcer</span>
            </button>
         
          </div>
        </div>

        {/* Search + stats */}
        <div className="glass-card flex-between">
          <div className="search-wrapper" style={{ minWidth: '220px', flex: 1, maxWidth: '100%' }}>
            <FontAwesomeIcon icon={faSearch} className="text-muted" />
            <input
              type="text"
              placeholder="Rechercher (nom, email, secteur...)"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <div className="text-secondary small" style={{ marginTop: '8px' }}>
            <span className="fw-bold">{pagination?.totalRecords || 0}</span> enregistrements
          </div>
        </div>

        {/* Grille de cartes */}
        <div className="glass-card p-3">
          {loading && !refreshing ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status" />
              <p className="text-secondary mt-2">Chargement...</p>
            </div>
          ) : entreprises.length === 0 ? (
            <div className="text-center py-5 text-secondary">Aucune entreprise trouvée.</div>
          ) : (
            <div className="cards-grid">
              {entreprises.map(ent => (
                <div key={ent.id} className="entreprise-card">
                  <div className="card-header">
                    <div className="card-title">
                      <FontAwesomeIcon icon={faBuilding} className="text-primary" />
                      {ent.nomEntreprise}
                      <span className="badge-id">#{ent.id}</span>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="info-row">
                      <span className="icon"><FontAwesomeIcon icon={faEnvelope} /></span>
                      {ent.email}
                    </div>
                    <div className="info-row">
                      <span className="icon"><FontAwesomeIcon icon={faPhone} /></span>
                      {ent.telephone}
                    </div>
                    <div className="info-row">
                      <span className="icon"><FontAwesomeIcon icon={faMapMarkerAlt} /></span>
                      {ent.adresse}
                    </div>
                    <div className="sector-badge">
                      <FontAwesomeIcon icon={faIndustry} className="me-1" />
                      {ent.secteur_activite}
                    </div>
                  </div>
                  <div className="card-actions">
                    <Link
                      to={`/infos_Entreprise/${ent.id}`}
                      className="monetra-btn icon-only"
                      title="Voir le profil"
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </Link>
                  
                    <button
                      className="monetra-btn icon-only primary"
                      onClick={() => openCandidatsModal(ent.id, ent.nomEntreprise)}
                      title="Voir les candidats placés"
                    >
                      <FontAwesomeIcon icon={faUserCheck} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          <div className="pagination-bar">
            <div className="page-info">
              Page <strong>{pagination?.currentPage || 1}</strong> sur <strong>{pagination?.totalPages || 1}</strong>
            </div>
            <div className="d-flex gap-2">
              <button
                className="monetra-btn icon-only"
                disabled={!pagination || pagination.currentPage <= 1 || loading || refreshing}
                onClick={() => fetchEntreprises(pagination.currentPage - 1, searchTerm, false)}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <button
                className="monetra-btn icon-only"
                disabled={!pagination || pagination.currentPage >= pagination.totalPages || loading || refreshing}
                onClick={() => fetchEntreprises(pagination.currentPage + 1, searchTerm, false)}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Entreprise */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>
              {editingId ? '✏️ Modifier' : '➕ Ajouter'} une entreprise
              <span className="sub">{editingId ? `#${editingId}` : ''}</span>
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nom de l'entreprise <span className="required">*</span></label>
                <input
                  type="text"
                  name="nomEntreprise"
                  value={form.nomEntreprise}
                  onChange={handleInputChange}
                  className={formErrors.nomEntreprise ? 'error' : ''}
                  disabled={loading || refreshing}
                />
                {formErrors.nomEntreprise && <div className="error-text">{formErrors.nomEntreprise}</div>}
              </div>
              <div className="form-group">
                <label>Email <span className="required">*</span></label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleInputChange}
                  className={formErrors.email ? 'error' : ''}
                  disabled={loading || refreshing}
                />
                {formErrors.email && <div className="error-text">{formErrors.email}</div>}
              </div>
              <div className="form-group">
                <label>Téléphone <span className="required">*</span></label>
                <input
                  type="text"
                  name="telephone"
                  value={form.telephone}
                  onChange={handleInputChange}
                  className={formErrors.telephone ? 'error' : ''}
                  disabled={loading || refreshing}
                />
                {formErrors.telephone && <div className="error-text">{formErrors.telephone}</div>}
              </div>
              <div className="form-group">
                <label>Adresse <span className="required">*</span></label>
                <input
                  type="text"
                  name="adresse"
                  value={form.adresse}
                  onChange={handleInputChange}
                  className={formErrors.adresse ? 'error' : ''}
                  disabled={loading || refreshing}
                />
                {formErrors.adresse && <div className="error-text">{formErrors.adresse}</div>}
              </div>
              <div className="form-group">
                <label>Secteur d'activité <span className="required">*</span></label>
                <select
                  name="secteur_activite"
                  value={form.secteur_activite}
                  onChange={handleInputChange}
                  className={formErrors.secteur_activite ? 'error' : ''}
                  disabled={loading || refreshing}
                >
                  <option value="">-- Sélectionnez --</option>
                  <option value="Agriculture">Agriculture</option>
                  <option value="Agroalimentaire">Agroalimentaire</option>
                  <option value="Bâtiment et Travaux Publics (BTP)">BTP</option>
                  <option value="Commerce de gros">Commerce de gros</option>
                  <option value="Commerce de détail">Commerce de détail</option>
                  <option value="Construction">Construction</option>
                  <option value="Éducation">Éducation</option>
                  <option value="Énergie">Énergie</option>
                  <option value="Environnement">Environnement</option>
                  <option value="Finance et Banque">Finance et Banque</option>
                  <option value="Hôtellerie">Hôtellerie</option>
                  <option value="Immobilier">Immobilier</option>
                  <option value="Industrie manufacturière">Industrie manufacturière</option>
                  <option value="Informatique et Technologies">Informatique</option>
                  <option value="Logistique et Transport">Logistique</option>
                  <option value="Médias et Communication">Médias</option>
                  <option value="Mines">Mines</option>
                  <option value="Pêche">Pêche</option>
                  <option value="Production industrielle">Production</option>
                  <option value="Recherche et Développement">R&D</option>
                  <option value="Restauration">Restauration</option>
                  <option value="Santé">Santé</option>
                  <option value="Sécurité privée">Sécurité</option>
                  <option value="Services administratifs">Services admin</option>
                  <option value="Services financiers">Services financiers</option>
                  <option value="Télécommunications">Télécoms</option>
                  <option value="Textile et Habillement">Textile</option>
                  <option value="Tourisme">Tourisme</option>
                  <option value="Transport">Transport</option>
                  <option value="Autres">Autres</option>
                </select>
                {formErrors.secteur_activite && <div className="error-text">{formErrors.secteur_activite}</div>}
              </div>
              <div className="d-flex justify-content-end gap-2 mt-3">
                <button
                  type="button"
                  className="monetra-btn"
                  onClick={() => setShowModal(false)}
                  disabled={loading || refreshing}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="monetra-btn primary"
                  disabled={loading || refreshing}
                >
                  {editingId ? 'Enregistrer' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Candidats Placés */}
      {showCandidatsModal && (
        <div className="modal-overlay" onClick={() => setShowCandidatsModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ position: 'relative' }}>
            <h3>
              <FontAwesomeIcon icon={faUserCheck} className="text-primary me-2" />
              Candidats placés – {selectedEntrepriseNom}
              <span className="sub">#ID {selectedEntrepriseId}</span>
            </h3>
            <button
              className="btn-close-custom"
              onClick={() => setShowCandidatsModal(false)}
              style={{ position: 'absolute', top: '1rem', right: '1.5rem', fontSize: '1.8rem', background: 'transparent', border: 'none', color: 'var(--text-secondary)' }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <CandidatsPlaces entrepriseId={selectedEntrepriseId} />
          </div>
        </div>
      )}

      {/* Toast */}
      {toast.show && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-primary' : 'text-danger'}
            />
            <span>{toast.message}</span>
            <button className="btn-close-custom" onClick={() => setToast({ ...toast, show: false })}>✕</button>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageEntreprisesPlacement;